import { useParams, Link } from 'react-router-dom';
import { movies, movieCategories, Movie } from '@/mocks/movies';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import SchemaOrg from '@/components/feature/SchemaOrg';

function getCategoryName(categoryId: string): string {
  const cat = movieCategories.find((c) => c.id === categoryId);
  return cat ? cat.name : '全部';
}

function MovieCard({ movie }: { movie: Movie }) {
  return (
    <Link
      to={`/article/${movie.id}`}
      className="group block cursor-pointer"
    >
      <div className="relative overflow-hidden rounded-lg aspect-[2/3] mb-3">
        <img
          src={movie.image}
          alt={movie.title}
          title={`${movie.title} - 萝卜影视 ${getCategoryName(movie.category)}高清在线观看`}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-3">
          <span className="text-white text-xs font-medium whitespace-nowrap">{movie.description.slice(0, 36)}...</span>
        </div>
        <div className="absolute top-2 right-2 bg-black/60 backdrop-blur-sm rounded-md px-2 py-0.5 text-accent-400 text-xs font-semibold whitespace-nowrap">
          {movie.rating}
        </div>
      </div>
      <h3 className="text-sm font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-1">
        {movie.title}
      </h3>
      <p className="text-xs text-foreground-500 mt-1 whitespace-nowrap">
        {movie.year} · {movie.duration}
      </p>
    </Link>
  );
}

export default function Category() {
  const { categoryId } = useParams<{ categoryId: string }>();
  const currentCategory = categoryId || 'all';
  const categoryName = getCategoryName(currentCategory);

  const filteredMovies = currentCategory === 'all'
    ? movies
    : movies.filter((m) => m.category === currentCategory);

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: '电影分类', href: '/category/all' },
    { label: categoryName },
  ];

  return (
    <>
      <ScrollToTop />
      <SchemaOrg
        type="WebPage"
        data={{
          name: `萝卜影视 - ${categoryName}电影`,
          description: `萝卜影视${categoryName}电影分类，精选${categoryName}类电影大全，高清在线观看${categoryName}电影。`,
          url: `https://luobo-movies.com/category/${currentCategory}`,
        }}
      />
      <SchemaOrg
        type="BreadcrumbList"
        data={{
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: '首页', item: 'https://luobo-movies.com/' },
            { '@type': 'ListItem', position: 2, name: '电影分类', item: 'https://luobo-movies.com/category/all' },
            { '@type': 'ListItem', position: 3, name: categoryName },
          ],
        }}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16">
          <Breadcrumb items={breadcrumbItems} />

          <section className="w-full px-4 md:px-6 py-6">
            <h1 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-2">
              {categoryName}电影
            </h1>
            <p className="text-sm text-foreground-500 mb-6">
              共 {filteredMovies.length} 部{categoryName === '全部' ? '' : categoryName}电影
            </p>

            <div className="flex items-center gap-2 flex-wrap mb-8 pb-6 border-b border-background-200/70">
              {movieCategories.map((cat) => (
                <Link
                  key={cat.id}
                  to={`/category/${cat.id}`}
                  className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-200 cursor-pointer ${
                    currentCategory === cat.id
                      ? 'bg-primary-500 text-white'
                      : 'bg-background-100 text-foreground-600 hover:bg-primary-100/70 hover:text-primary-600'
                  }`}
                >
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className={`${cat.icon} text-xs`}></i>
                  </span>
                  {cat.name}
                </Link>
              ))}
            </div>

            {filteredMovies.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4 md:gap-5">
                {filteredMovies.map((movie) => (
                  <MovieCard key={movie.id} movie={movie} />
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center text-foreground-300">
                  <i className="ri-movie-line text-4xl"></i>
                </div>
                <p className="text-foreground-500 text-sm">该分类暂无电影，请选择其他分类</p>
              </div>
            )}
          </section>
        </main>
        <Footer />
      </div>
    </>
  );
}